/**
 * The clinical-tasks PERSISTED-STORAGE vocabulary and its scoping rule — the
 * zero-jotai half of what used to be `ui/clinicalTasksAtoms.ts`.
 *
 * RULED by the programme owner (2026-08-30, ruling 40 §6): bundler
 * tree-shaking is not an architectural boundary. `pages/_app`'s module graph
 * must not reach the `.` UI door AT ALL, and it did — `src/utils/auth/
 * logoutStorage.ts` needs a key prefix and a scope string to sweep a
 * clinician's saved task filters on logout, and reached them through
 * `@/atoms/clinicalTasksAtoms`, which forwards the `.` door, which pulls
 * twenty rendered components and seventeen design-system doors behind it.
 * `sideEffects: false` made that free in the BUNDLE and changed nothing about
 * the graph; this module is the severance.
 *
 * THIS FILE IS INERT AND IMPORTS NOTHING, which is the `/contracts` rule
 * (`manifest-boundary.test.ts`: relative modules and `@heidi/schema` only) and
 * is also what makes it safe for a logout path to reach. That constraint is
 * what shapes the two functions below, and the shape is worth stating because
 * it is not what a reader would write first:
 *
 *   - `getClinicalTasksStorageScope` USED to read `getWorkspaceId()` and
 *     `getCurrentUser()` from `@heidi/workspace/data` itself. It cannot here —
 *     that is a foreign domain door and `/contracts` may not import one — so
 *     the rule is a pure function OVER an identity the caller supplies, and it
 *     is named `buildClinicalTasksStorageScope` rather than keeping the old
 *     name at a new arity. Two same-named exports on two doors differing only
 *     in signature is a trap, and the app-bound zero-argument
 *     `getClinicalTasksStorageScope` still exists, unchanged, in
 *     `ui/clinicalTasksAtoms.ts` for every caller that had one.
 *   - `createClinicalTasksScopedStorage` takes its storage base AND its
 *     identity reader, for the same reason: `getSafeLocalStorage` lives behind
 *     `@heidi/utils/jotai-storage`.
 *
 * INJECTION RATHER THAN REGISTRATION, and the alternative is worth naming
 * because it is the obvious one. A module-scope adapter slot here — the
 * `registerCurrentIdsAdapter` shape — would have let the old zero-argument
 * signature survive verbatim. It was refused: whoever registers has to be
 * loaded before the first read, the only module that knows the readers is the
 * UI zone this change exists to unreach, and an unregistered slot fails by
 * returning `null`, which makes the logout sweep sweep NOTHING and say so
 * nowhere. On a shared clinical workstation that leaves one clinician's saved
 * filters for the next. A required parameter cannot be forgotten at runtime
 * because it cannot be forgotten at compile time.
 *
 * The keys themselves are byte-identical to their previous declarations and
 * this is their ONE site; `ui/clinicalTasksAtoms.ts` re-exports them from here.
 * `persistence-manifest.json` pins them, so a re-key is caught by
 * `pnpm persistence:check` rather than by a clinician losing their filters.
 */

/**
 * Shared by every persisted clinical-tasks preference key below. Logout
 * cleanup (`clearLogoutStorage`) scans localStorage by this prefix instead of
 * enumerating each key, so a future key added here can't be missed there.
 */
export const CLINICAL_TASKS_STORAGE_KEY_PREFIX = 'heidi-clinical-tasks-';

export const CLINICAL_TASKS_SORT_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}sort`;
export const CLINICAL_TASKS_STATUS_FILTER_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}status-filter`;
export const CLINICAL_TASKS_STATUS_FILTER_V2_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}status-filter-v2`;
export const CLINICAL_TASKS_ASSIGNMENT_FILTER_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}assignment-filter`;
export const CLINICAL_TASKS_CATEGORY_FILTER_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}category-filter`;
export const CLINICAL_TASKS_DUE_DATE_RANGE_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}due-date-range`;
export const CLINICAL_TASKS_DUE_DATE_PRESET_KEY = `${CLINICAL_TASKS_STORAGE_KEY_PREFIX}due-date-preset`;

/**
 * Who the persisted preferences belong to. Both members are deliberately
 * `null | undefined`-tolerant: the callers resolve them from a cache and a
 * localStorage read, either of which can legitimately be unresolved.
 */
export interface ClinicalTasksStorageIdentity {
  readonly workspaceId: string | null | undefined;
  readonly userId: string | null | undefined;
}

/** The `Storage`-shaped surface the scoped adapter wraps. Structural, so a real `localStorage`, a `getSafeLocalStorage()` and a test double all satisfy it. */
export interface ClinicalTasksStorageBase {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/**
 * Returns `workspaceId:userId`, or `null` while either is still resolving
 * (e.g. on first mount before GET_USER has hydrated). Callers must not fall
 * back to a shared placeholder scope here: a shared bucket would either leak
 * one session's prefs to the next unresolved session on a shared clinical
 * workstation, or silently apply another session's stale prefs. See
 * `createClinicalTasksScopedStorage`, which skips persistence entirely when
 * this returns `null`.
 */
export function buildClinicalTasksStorageScope(
  identity: ClinicalTasksStorageIdentity
): string | null {
  const { workspaceId, userId } = identity;
  return workspaceId && userId ? `${workspaceId}:${userId}` : null;
}

/**
 * Raw scoped storage adapter, exported (in addition to being used by the atom
 * module's `createScopedTaskStorage`) so scoping/isolation can be unit-tested
 * directly: Jest's project-wide `__mocks__/jotai/utils.js` replaces
 * `atomWithStorage`/`createJSONStorage` with a version that ignores the
 * custom storage argument entirely, so this logic can never be exercised by
 * mounting the real atoms in the app's jest project.
 *
 * `readIdentity` is a thunk rather than a value because the scope resolves
 * LATE — an adapter is built once at module scope and read long afterwards,
 * by which time a workspace or user that was unresolved has arrived. Passing
 * the identity by value would freeze the unresolved one and persist nothing
 * for the rest of the session.
 */
export function createClinicalTasksScopedStorage(
  base: ClinicalTasksStorageBase,
  readIdentity: () => ClinicalTasksStorageIdentity
): ClinicalTasksStorageBase {
  return {
    getItem: (key: string) => {
      const scope = buildClinicalTasksStorageScope(readIdentity());
      return scope ? base.getItem(`${key}:${scope}`) : null;
    },
    setItem: (key: string, value: string) => {
      const scope = buildClinicalTasksStorageScope(readIdentity());
      if (scope) {
        base.setItem(`${key}:${scope}`, value);
      }
    },
    removeItem: (key: string) => {
      const scope = buildClinicalTasksStorageScope(readIdentity());
      if (scope) {
        base.removeItem(`${key}:${scope}`);
      }
    },
  };
}

/**
 * `JSON.parse` doesn't revive ISO date strings back into `Date` instances,
 * so a `DateRange` atom persisted via plain JSON storage would silently
 * hand consumers strings instead of `Date`s after a hydration round trip.
 * Exported so `useHydrateClinicalTaskPrefs`'s manual `JSON.parse` (which
 * bypasses this storage's own reviver) can apply the same conversion.
 */
export function dateRangeReviver(key: string, value: unknown): unknown {
  if ((key === 'from' || key === 'to') && typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return value;
}
